import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { TermSheet } from 'components/UI/TermSheet'

interface TermSheetApi {
  /** Open the sheet on a glossary id. Replaces whatever it was showing. */
  openTerm: (termId: string) => void
}

/*
 * `null` default, and consumers throw on it rather than silently no-opping.
 *
 * A default of `{ openTerm: () => {} }` would make a `TermInfo` rendered outside the
 * provider look fine and do nothing when tapped — the exact "broken, not absent" failure
 * a dead menu row causes. Failing loudly means the mistake is found the first time the
 * component is mounted, not by a user tapping a button that ignores them.
 */
const TermSheetContext = createContext<TermSheetApi | null>(null)

export const useTermSheet = (): TermSheetApi => {
  const ctx = useContext(TermSheetContext)
  if (!ctx) {
    throw new Error('useTermSheet must be used inside <TermSheetProvider>')
  }
  return ctx
}

/**
 * Owns the app's ONE `TermSheet`, and that singularity is the mechanism.
 *
 * ## Why a provider rather than a sheet per call site
 *
 * §52 caps bottom sheets at two levels. `TermSheet` already handles one half of that —
 * following a related term swaps its body instead of opening another sheet — but that
 * only stops the sheet stacking on ITSELF. It says nothing about the other half: an `(i)`
 * on the certificate page and an `(i)` inside some future sheet each rendering their own
 * `<TermSheet>`, which stacks two definitions with no code path noticing.
 *
 * With one instance, that is not a rule anyone has to remember. There is a single sheet
 * and a single `termId`, so a second `openTerm` REPLACES the first — the same swap the
 * Related row does. Stacking two term sheets is structurally impossible, not merely
 * avoided by convention, and every convention in this repo that had to be remembered
 * under load (§41, §45, §54) is recorded as having failed at least once.
 *
 * It also means an `(i)` costs one button. A per-call-site sheet would put a mounted
 * `DrawerPopup` — an MUI `SwipeableDrawer` with its own Modal machinery — behind every
 * info glyph on a page that may carry half a dozen.
 *
 * ## Where to mount it
 *
 * Once, inside the router: `TermSheet` calls `useNavigate` for its "Learn more" link, so
 * a mount above `<Routes>`' Router would throw. Inside `ThemeProvider` too, since the
 * sheet is themed by the tenant like everything else.
 */
export const TermSheetProvider = ({ children }: { children: React.ReactNode }) => {
  const [termId, setTermId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  const openTerm = useCallback((id: string) => {
    setTermId(id)
    setOpen(true)
  }, [])

  // Close does NOT clear `termId`. Clearing it would unmount `TermSheet` mid-transition,
  // so MUI's exit Slide would have nothing left to animate and the sheet would vanish
  // rather than sliding away — the same defect recorded on the certificates page's log-out
  // drawer. The id is harmless while closed and is replaced on the next open.
  const close = useCallback(() => setOpen(false), [])

  const api = useMemo<TermSheetApi>(() => ({ openTerm }), [openTerm])

  return (
    <TermSheetContext.Provider value={api}>
      {children}
      {termId && <TermSheet termId={termId} open={open} onClose={close} />}
    </TermSheetContext.Provider>
  )
}
